import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { createHash } from 'crypto';
import { AuditLogger } from '../utils/AuditLogger';

interface SignatureOptions {
  type: 'DRAWN' | 'TYPED';
  data: string;
  signerId: string;
  timestamp: Date;
  geoLocation?: {
    latitude: number;
    longitude: number;
  };
}

interface SignatureMetadata {
  signerId: string;
  timestamp: Date;
  ipAddress: string;
  userAgent: string;
  geoLocation?: {
    latitude: number;
    longitude: number;
  };
  deviceInfo: any;
  hash: string;
}

export class SignatureService {
  private auditLogger: AuditLogger;

  constructor() {
    this.auditLogger = new AuditLogger();
  }

  async createSignature(options: SignatureOptions): Promise<{
    signatureImage: Buffer;
    metadata: SignatureMetadata;
  }> {
    try {
      let signatureImage: Buffer;

      switch (options.type) {
        case 'DRAWN':
          signatureImage = await this.processDrawnSignature(options.data);
          break;
        case 'TYPED':
          signatureImage = await this.generateTypedSignature(options.data);
          break;
        default:
          throw new Error('Invalid signature type');
      }

      const metadata = await this.generateSignatureMetadata(options, signatureImage);
      
      // Log signature creation
      await this.auditLogger.log(
        'SIGNATURE_CREATED', 
        {
          userId: options.signerId,
          details: {
            type: options.type,
            timestamp: options.timestamp,
            metadata
          }
        }
      );

      return {
        signatureImage,
        metadata
      };
    } catch (error) {
      console.error('Error creating signature:', error);
      throw error;
    }
  }

  private async processDrawnSignature(dataUrl: string): Promise<Buffer> {
    try {
      // Remove data URL prefix if present
      const base64Data = dataUrl.replace(/^data:image\/png;base64,/, '');
      return Buffer.from(base64Data, 'base64');
    } catch (error) {
      console.error('Error processing drawn signature:', error);
      throw error;
    }
  }

  private async generateTypedSignature(text: string): Promise<Buffer> {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([400, 100]);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      
      page.drawText(text, {
        x: 50,
        y: 50,
        size: 30,
        font,
        color: rgb(0, 0, 0)
      });

      const pdfBytes = await pdfDoc.save();
      return Buffer.from(pdfBytes);
    } catch (error) {
      console.error('Error generating typed signature:', error);
      throw error;
    }
  }

  private async generateSignatureMetadata(
    options: SignatureOptions,
    signatureImage: Buffer
  ): Promise<SignatureMetadata> {
    const hash = createHash('sha256')
      .update(signatureImage)
      .update(options.signerId)
      .update(options.timestamp.toISOString())
      .digest('hex');

    return {
      signerId: options.signerId,
      timestamp: options.timestamp,
      ipAddress: await this.getClientIP(),
      userAgent: await this.getClientUserAgent(),
      geoLocation: options.geoLocation,
      deviceInfo: await this.getDeviceInfo(),
      hash
    };
  }

  private async getClientIP(): Promise<string> {
    // Implementation for getting client IP
    return '127.0.0.1'; // Placeholder
  }

  private async getClientUserAgent(): Promise<string> {
    // Implementation for getting user agent
    return navigator.userAgent;
  }

  private async getDeviceInfo(): Promise<any> {
    // Implementation for getting device info
    return {
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      // Add other relevant device information
    };
  }

  async validateSignature(
    pdfBuffer: Buffer,
    originalMetadata: SignatureMetadata
  ): Promise<boolean> {
    const pdfDoc = await PDFDocument.load(pdfBuffer);
    const pages = pdfDoc.getPages();
    
    // Verify signature hasn't been tampered with
    const hash = createHash('sha256')
      .update(pdfBuffer)
      .update(originalMetadata.signerId)
      .update(originalMetadata.timestamp.toISOString())
      .digest('hex');
    
    return hash === originalMetadata.hash;
  }
} 